import React from 'react';
import '../pages/RegistrationPage.css';

/**
 * Twilio A2P 10DLC consent, shown before any SMS path is set in motion. Shared
 * by every flow that can result in a message:
 *   - registration        (RegistrationPage)  — default copy below
 *   - login-time MFA      (MfaSetupPage)      — default copy below
 *   - host waitlist       (SMRTHost)          — HOST_WAITLIST_CONSENT_TEXT
 *
 * Each text is kept in sync with its canonical twin in
 * askSmart-backend/src/utils/smsConsent.ts, which holds the copy that is
 * actually recorded, along with its consent version. This consent is
 * deliberately separate from the Terms & Conditions / Privacy Policy checkbox.
 */

/** Account verification / MFA codes — backend version 'sms-mfa-v1'. */
export const SMS_CONSENT_TEXT =
  'I agree to receive a one-time transactional SMS message containing an account ' +
  'verification and multi-factor authentication (MFA) code. Additional verification ' +
  'messages will only be sent if I request another verification code. Message and ' +
  'data rates may apply. Reply STOP to opt out or HELP for assistance.';

/**
 * SMRT Host "your table is ready" notification — backend version
 * 'sms-host-waitlist-v1'. Attested by staff on the guest's behalf, and covers
 * exactly one notification for that guest visit.
 */
export const HOST_WAITLIST_CONSENT_TEXT =
  'I confirm that the guest has agreed to receive one transactional SMS ' +
  'notification when their table is ready. Message and data rates may apply. ' +
  'Reply STOP to opt out or HELP for assistance.';

interface Props {
  /** Render nothing when false — the modal is never pre-accepted or auto-confirmed. */
  show: boolean;
  /** Close without recording consent or making any request. */
  onCancel: () => void;
  /** Affirmative consent — the caller records it and then proceeds. */
  onAgree: () => void;
  /** Disables the confirm button while the request is in flight. */
  disabled?: boolean;
  title?: string;
  consentText?: string;
  confirmLabel?: string;
}

export default function SmsConsentModal({
  show,
  onCancel,
  onAgree,
  disabled,
  title = 'Confirm SMS Verification',
  consentText = SMS_CONSENT_TEXT,
  confirmLabel = 'Agree & Send Code',
}: Props) {
  if (!show) return null;

  return (
    <div className="popup-overlay">
      <div className="sms-consent-popup" role="dialog" aria-modal="true" aria-labelledby="sms-consent-title">
        <h3 id="sms-consent-title">{title}</h3>
        <p className="sms-consent-text">{consentText}</p>
        <div className="sms-consent-actions">
          <button type="button" className="existing-user-cancel-btn" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className="sms-consent-agree-btn"
            disabled={disabled}
            onClick={onAgree}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
